const BASIC_LAND_NAMES = new Set([
    'Plains',
    'Island',
    'Swamp',
    'Mountain',
    'Forest',
    'Wastes',
    'Snow-Covered Plains',
    'Snow-Covered Island',
    'Snow-Covered Swamp',
    'Snow-Covered Mountain',
    'Snow-Covered Forest',
]);
export function isBasicLand(card) {
    return BASIC_LAND_NAMES.has(card.name);
}
/**
 * Returns true if the card can be played as a land from hand.
 *
 * Correctly handles multi-face cards:
 * - `modal_dfc`: true if either face is a Land (both faces are playable from hand).
 * - Other multi-face layouts (`transform`, `adventure`, `flip`, `split`, etc.):
 *   only the FRONT face is playable from hand, so only its type matters.
 *   Cards like Ojer Axonil (creature that transforms into a land when it dies)
 *   are NOT lands by this definition — you can't cast them as a land.
 * - Single-face cards: uses the typeLine directly.
 */
export function isLand(card) {
    if (card.layout === 'modal_dfc') {
        return card.cardFaces?.some((f) => f.typeLine.toLowerCase().includes('land')) ?? false;
    }
    if (card.cardFaces && card.cardFaces.length >= 2) {
        return card.cardFaces[0]?.typeLine.toLowerCase().includes('land') ?? false;
    }
    return card.typeLine.toLowerCase().includes('land');
}
export function isCreature(card) {
    return card.typeLine.includes('Creature');
}
export function isLegendary(card) {
    return card.typeLine.includes('Legendary');
}
export function isLegalCommander(card) {
    // Legendary creatures and certain planeswalkers/vehicles with is:commander
    if (isLegendary(card) && isCreature(card))
        return true;
    // Check for "can be your commander" oracle text (e.g. Kenrith, Grist, Shorikai)
    const text = card.oracleText ?? card.cardFaces?.[0]?.oracleText ?? '';
    if (text.includes('can be your commander'))
        return true;
    return false;
}
/**
 * Whether a card may be added to the "Want to Build" commander list from Set
 * Review. Broader than {@link isLegalCommander}: in addition to legal commanders
 * it also accepts legendary Vehicles, which Dennis wants to flag for building
 * even though they are not commander-legal on their own.
 */
export function isWantToBuildEligible(card) {
    if (isLegalCommander(card))
        return true;
    if (isLegendary(card) && card.typeLine.includes('Vehicle'))
        return true;
    return false;
}
/**
 * Normalize a commander card name to its EDHREC slug.
 *
 * EDHREC uses only the front-face name for DFC commanders. The transform must
 * stay identical across server and client so verdicts recorded from Set Review
 * match those recorded from Commander Discovery. This is the single source of
 * truth; the server re-exports it via the edhrec-client.
 */
export function normalizeCommanderSlug(name) {
    const frontFace = (name.split('//')[0] ?? name).trim();
    return frontFace
        .toLowerCase()
        .replace(/[,.':"\/]/g, '') // strip commas, apostrophes, periods, colons, quotes, slashes
        .replace(/\s+/g, '-') // spaces → hyphens
        .replace(/-+/g, '-') // collapse consecutive hyphens
        .replace(/^-|-$/g, ''); // trim leading/trailing hyphens
}
export function hasPartnerKeyword(card) {
    return (card.keywords.includes('Partner') ||
        card.keywords.some((k) => k.startsWith('Partner with')) ||
        card.keywords.includes('Friends forever') ||
        card.keywords.some((k) => k.startsWith('Choose a Background')));
}
export function isPartnerCompatible(a, b) {
    // Both have Partner (generic)
    if (card_hasGenericPartner(a) && card_hasGenericPartner(b))
        return true;
    // Friends forever — any two FF cards
    if (a.keywords.includes('Friends forever') && b.keywords.includes('Friends forever'))
        return true;
    // Partner with — named partner
    const aWith = a.keywords.find((k) => k.startsWith('Partner with '));
    const bWith = b.keywords.find((k) => k.startsWith('Partner with '));
    if (aWith && bWith) {
        return aWith === `Partner with ${b.name}` && bWith === `Partner with ${a.name}`;
    }
    // Choose a Background — one is a legendary background, one has choose a background
    const aChoose = a.keywords.some((k) => k.startsWith('Choose a Background'));
    const bChoose = b.keywords.some((k) => k.startsWith('Choose a Background'));
    if (aChoose && b.typeLine.includes('Background'))
        return true;
    if (bChoose && a.typeLine.includes('Background'))
        return true;
    return false;
}
function card_hasGenericPartner(card) {
    return card.keywords.includes('Partner') && !card.keywords.some((k) => k.startsWith('Partner with'));
}
export function getUsdPrice(card) {
    const price = card.prices.usd;
    if (!price)
        return null;
    const parsed = parseFloat(price);
    return isNaN(parsed) ? null : parsed;
}
/**
 * Returns true if the card has a printed power/toughness on its front face.
 * Handles DFC cards by checking the first card face.
 * Vehicles are included because they have a P/T box.
 */
export function cardHasPrintedPT(card) {
    const typeLine = card.typeLine || card.cardFaces?.[0]?.typeLine || '';
    return typeLine.includes('Creature') || typeLine.includes('Vehicle');
}
/**
 * Returns the P/T to display for a card instance on the battlefield.
 *
 * Resolution order:
 * 1. If the instance has a numeric override, use it.
 * 2. If the card has a printed P/T (or its front face does), use the printed string.
 * 3. Otherwise, returns null (don't show the badge).
 *
 * Face-down cards: show the override if set; otherwise return null
 * (a face-down card hides its P/T in Magic, though it is a 2/2 — we keep it simple).
 *
 * @returns `{ power: string; toughness: string }` to render, or `null` to hide the badge.
 */
export function getInstancePTDisplay(card, instance) {
    const hasOverride = instance.power !== undefined || instance.toughness !== undefined;
    if (instance.faceDown) {
        if (!hasOverride)
            return null;
        return {
            power: String(instance.power ?? 0),
            toughness: String(instance.toughness ?? 0),
        };
    }
    if (hasOverride) {
        return {
            power: String(instance.power ?? 0),
            toughness: String(instance.toughness ?? 0),
        };
    }
    // Use printed value — check top-level or front face
    const printedPower = card.power ?? card.cardFaces?.[0]?.power;
    const printedToughness = card.toughness ?? card.cardFaces?.[0]?.toughness;
    if (printedPower !== undefined && printedToughness !== undefined) {
        return { power: printedPower, toughness: printedToughness };
    }
    return null;
}
/**
 * Parse a printed P/T string into a numeric seed value for overrides.
 * Plain integers are returned as-is. Non-numeric values (*, X, 1+*) seed to 0.
 */
export function parsePrintedPTValue(printed) {
    if (printed === undefined)
        return 0;
    const n = parseInt(printed, 10);
    return isNaN(n) ? 0 : n;
}
//# sourceMappingURL=card-helpers.js.map